import { memo, useCallback } from 'react';

import { cn } from '@air/utils-tailwind';

import { SearchSubSideNavSearchAllItem } from '~/components/v2/components/workspace/search-nav/item/SearchSubSideNavSearchAllItem';
import { SearchSubSideNavItems } from '~/components/v2/components/workspace/search-nav/items/SearchSubSideNavItems';
import { SearchSubSideNavSavedSearches } from '~/components/v2/components/workspace/search-nav/items/SearchSubSideNavSavedSearches';
import {
  useWorkspaceSidebarSectionStore,
  WorkspaceSidebarSectionStoreContextProvider,
} from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { ZustandStorageKeys } from '~/constants/zustandStorageKeys';

export const SearchSubSideNavPanel = memo(() => {
  const collapsed = useWorkspaceSidebarSectionStore((store) => store.collapsed);

  const setScrollElement = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        node.inert = collapsed;
      }
    },
    [collapsed],
  );

  return (
    // Capped at 70% of the sub-nav so Favorites and Libraries stay on screen. The list scrolls past that.
    <section
      className="flex max-h-[70%] min-h-0 shrink-0 flex-col overflow-hidden ads:border-default border-gray-4/80 border-b"
      data-testid="SEARCH_SUB_SIDE_NAV"
    >
      <SearchSubSideNavSearchAllItem />
      <WorkspaceSidebarSectionStoreContextProvider
        defaultCollapsed
        storageKey={ZustandStorageKeys.WorkspaceSidebarSectionSavedSearches}
      >
        {/* 0fr → 1fr animates this block's height so Favorites and Libraries slide with it.
            The list stays mounted; min-h-0 lets the open row shrink inside the 70% cap and scroll. */}
        <div
          className={cn(
            'grid min-h-0 transition-[grid-template-rows] duration-200 ease-out',
            collapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
          )}
        >
          <div className="flex min-h-0 flex-col overflow-hidden">
            <div
              className="scrollbar-thin scrollbar-track-gray-2 scrollbar-thumb-gray-7 [&:not(:hover)]:scrollbar-track-transparent flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain px-2.5 pb-2"
              data-testid="SEARCH_SUB_SIDE_NAV_SCROLL"
              ref={setScrollElement}
            >
              <SearchSubSideNavItems />
              {!collapsed && <SearchSubSideNavSavedSearches />}
            </div>
          </div>
        </div>
      </WorkspaceSidebarSectionStoreContextProvider>
    </section>
  );
});

SearchSubSideNavPanel.displayName = 'SearchSubSideNavPanel';
